import { useMemo } from 'react';

import { useSelector } from 'react-redux';

import { USD } from '../../../../constants/currency-codes';
import { InsuranceProviders, getInsuranceProviderByName } from '../../../../enums/insurance-providers';
import { willShipmentPassThroughMexico } from '../../../../fulfillment-planner/package-content-description/use-should-require-package-content-description';
import { calculateTotalInsuredValue } from '../../../../services/insurance-service';
import { OtherShippingOptionsService } from '../../../../services/other-shipping-options/other-shipping-options-service';
import { convertAndSumWeightValues } from '../../../../services/unit-services/weights/weight-convert-and-sum-service';
import Global from '../../../../store/global-selectors';
import { usePrepaidDutyAndPrice } from '../../../../fulfillment-planner/hooks/use-prepaid-duty-and-price';

const NO_CONFIRMATION_OPTION_NAME = 'None';

const useShipFrom = (shipFromId, shipFromNameFallback) => {
  return useSelector((state) => {
    const shipFromEntity = Global.selectSellerShipFromsById(state, {
      shipFromId,
    });
    return shipFromEntity?.name || shipFromNameFallback;
  });
};

const useWeight = (packages) => {
  const weightUnit = useSelector((state) => Global.selectSellerDefaultWeightUnit(state));
  const weights = packages.map((p) => p.weight);

  const { value, unit } = convertAndSumWeightValues(weights, weightUnit);

  return useMemo(() => {
    return {
      value,
      unit,
    };
  }, [unit, value]);
};

const useSize = (dimensions) => {
  return useMemo(() => {
    return {
      length: dimensions?.length || 0,
      width: dimensions?.width || 0,
      height: dimensions?.height || 0,
      unit: dimensions?.unit,
    };
  }, [dimensions?.height, dimensions?.length, dimensions?.unit, dimensions?.width]);
};

const usePackageType = (packages) => {
  return useSelector((state) => {
    const sellerPackageType = Global.selectSellerPackageType(state, {
      packages,
    });
    return sellerPackageType?.name;
  });
};

const useConfirmation = (carrierId, confirmationEnumId, confirmationNameFallback) => {
  return useSelector((state) => {
    const carriers = Global.selectReferenceDatagetAllTheCarriers(state);
    const carrier = carriers.find((c) => c.carrierId === carrierId);

    let confirmationId;

    if (confirmationEnumId) {
      confirmationId = confirmationEnumId;
    }

    if (confirmationNameFallback === NO_CONFIRMATION_OPTION_NAME) {
      confirmationId = 0;
    }

    return OtherShippingOptionsService.getConfirmationTextForCarrier(carrier, confirmationId);
  });
};

const useInsurance = (insuranceProviderId, insuranceProviderNameFallback, packages, insuranceBookingId, insuranceClaimInitiationUrl) => {
  const sellerCurrencyCode = useSelector((state) => Global.selectSellerDefaultCurrency(state)) || USD;

  let safeInsuranceProviderId;
  if (insuranceProviderId) {
    safeInsuranceProviderId = insuranceProviderId;
  } else {
    const providerFromFulfillmentPlan = getInsuranceProviderByName(insuranceProviderNameFallback);
    safeInsuranceProviderId = providerFromFulfillmentPlan?.insuranceProviderId || InsuranceProviders.None.insuranceProviderId;
  }

  const insuredValue = calculateTotalInsuredValue(packages, safeInsuranceProviderId, sellerCurrencyCode);

  return useMemo(() => {
    return {
      insuranceProviderId: safeInsuranceProviderId,
      value: insuredValue.value,
      currencyCode: insuredValue.code,
      insuranceBookingId,
      insuranceClaimInitiationUrl,
    };
  }, [safeInsuranceProviderId, insuredValue.code, insuredValue.value, insuranceBookingId, insuranceClaimInitiationUrl]);
};

export const useShipmentDisplay = ({
  packages,
  shipFromId,
  size,
  carrierId,
  confirmationEnumId,
  confirmationNameFallback,
  insuranceProviderId,
  insuranceProviderNameFallback,
  trackingStatus,
  isReturn,
  shipTo,
  fulfillmentShipFrom,
  availableShippingOptions,
  options,
  insuranceBookingId,
  insuranceClaimInitiationUrl,
}) => {
  const isMultiPackage = packages.length > 1;

  const shipFrom = useShipFrom(shipFromId, fulfillmentShipFrom?.name);
  const weight = useWeight(packages);
  const dimensions = useSize(size);
  const packageType = usePackageType(packages);
  const confirmation = useConfirmation(carrierId, confirmationEnumId, confirmationNameFallback);
  const insurance = useInsurance(
    insuranceProviderId,
    insuranceProviderNameFallback,
    packages,
    insuranceBookingId,
    insuranceClaimInitiationUrl,
  );
  // After a investigation about how packages could be empty in SPD-20854, there are cases where sellers set packages as null/none/empty
  // This could be done with automation rules or production mapping, so we need to handle this case  const packageContentDescription = isMultiPackage ? null : packages[0]?.contentDescription;
  const packageContentDescription = isMultiPackage ? null : packages[0]?.contentDescription;
  const isPackageDescriptionRequired = willShipmentPassThroughMexico(fulfillmentShipFrom?.countryCode, shipTo?.countryCode);
  const prepaidDutyAndPrice = usePrepaidDutyAndPrice({
    availableShippingOptions,
    options,
  });

  return useMemo(() => {
    return {
      isMultiPackage,
      trackingStatus,
      shipFrom,
      weight,
      dimensions,
      packageType,
      confirmation,
      insurance,
      isReturn,
      packageContentDescription,
      isPackageDescriptionRequired,
      prepaidDutyAndPrice,
    };
  }, [
    isMultiPackage,
    trackingStatus,
    shipFrom,
    weight,
    dimensions,
    packageType,
    confirmation,
    insurance,
    isReturn,
    packageContentDescription,
    isPackageDescriptionRequired,
    prepaidDutyAndPrice,
  ]);
};
